import { ContextDisplayNames } from "@/Chat/ChatSidebar/hooks/useRobotoLLMContext";
import { RobotoLLMContext } from "@/shared/domain/chat/ChatRecord";
import { pluralize, truncateMiddle } from "@/shared/text";

import { type ContextType } from "../contextType";
import { getVisualizerPanelCount, isEmptyContext } from "../contextUtils";

import { ContextGroup, ContextItem } from "./ContextGroup";
import styles from "./ContextWidget.module.css";

interface ContextWidgetProps {
  context: RobotoLLMContext;
  displayNames: ContextDisplayNames;
  onDelete: (contextType: ContextType, contextContent: string) => void;
}

/**
 * Widget that displays the current RobotoLLMContext grouped by type.
 */
export const ContextWidget = (props: ContextWidgetProps) => {
  if (isEmptyContext(props.context)) {
    return null;
  }

  // Transform LLM context to displayable context groups
  const contextGroups = toContextGroups(props.context, props.displayNames);

  return (
    <div className={styles.contextContainer}>
      {/* Visualizer group */}
      <ContextGroup
        contextType="viz_state"
        items={contextGroups.visualizer}
        onDelete={() => props.onDelete("viz_state", "")}
      />

      {/* Dataset group */}
      <ContextGroup
        contextType="dataset"
        items={contextGroups.datasets}
        onDelete={(id) => props.onDelete("dataset", id)}
      />

      {/* Files group */}
      <ContextGroup
        contextType="file"
        items={contextGroups.files}
        onDelete={(id) => props.onDelete("file", id)}
      />
    </div>
  );
};

interface ContextGroups {
  datasets: ContextItem[];
  files: ContextItem[];
  visualizer: ContextItem[];
}

const MAX_FILE_PATH_LENGTH = 20;

/**
 * Transforms RobotoLLMContext into context items grouped by type.
 */
function toContextGroups(
  context: RobotoLLMContext,
  displayNames: ContextDisplayNames,
): ContextGroups {
  // Prepare dataset items
  const datasets = context.dataset_ids.map((datasetId) => ({
    id: datasetId,
    label: datasetId,
    tooltip: displayNames.datasets[datasetId],
  }));

  // Prepare file items
  const files = context.file_ids.map((fileId) => {
    const filePath = displayNames.files[fileId] ?? fileId;
    const needsTruncation = filePath.length > MAX_FILE_PATH_LENGTH;
    return {
      id: fileId,
      label: truncateMiddle(filePath, MAX_FILE_PATH_LENGTH),
      tooltip: needsTruncation ? filePath : undefined,
    };
  });

  // Prepare visualizer items
  const visualizer: ContextItem[] = [];
  if (context.visualizer_state) {
    const numPanels = getVisualizerPanelCount(context.visualizer_state);

    visualizer.push({
      id: "viz_state",
      label:
        numPanels === 0
          ? "empty_visualizer"
          : pluralize(`${numPanels}_viz_panel`, numPanels),
      tooltip: undefined,
    });
  }

  return { datasets, files, visualizer };
}
